import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";
import { REGIONS, IMAGES } from "@/lib/content";

export default function ServiceAreas() {
  const { t } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="areas" ref={ref} className="relative py-24 md:py-36 bg-neutral-950 text-white overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0 opacity-25 scale-110">
        <img src={IMAGES.stuttgart} alt="Region Stuttgart" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-neutral-950/60" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-ibk" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-ibk">{t.areas.label}</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl">{t.areas.title}</h2>
          <p className="mt-4 text-white/70 max-w-xl">{t.areas.sub}</p>
        </Reveal>
      </div>

      {/* Editorial marquee */}
      <div className="relative z-10 mt-16 md:mt-24 select-none" data-testid="areas-marquee">
        <Marquee speed={38} gradient={false} autoFill>
          {REGIONS.map((r, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-8 font-serif text-6xl sm:text-8xl leading-none text-white/90">
              {r}
              <span className="text-ibk">✳</span>
            </span>
          ))}
        </Marquee>
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 mt-16 md:mt-24">
        <div className="flex flex-wrap gap-3">
          {REGIONS.map((r, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-5 py-2.5 text-sm backdrop-blur-sm" data-testid={`region-chip-${i}`}>
                <MapPin size={14} className="text-ibk" /> {r}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
